<template>
  <NavigationBackButton />
  <div class="pending-applications">
    <!-- Main Card -->
    <div class="rounded-lg mt-3 border-0 m-4">
      <!-- Header Section -->
      <div class="card-header bg-gradient  shadow rounded-lg mt-2 py-2">
        <div class="nav-tabs-wrapper">
          <div class="d-flex justify-content-between align-items-center">

            <div class="d-flex flex-column">
              <p v-if="entity_type_name" class="mb-0 fw-light text-white text-xxs lh-1">
                {{ entity_type_name }}
              </p>
              <p class="mb-0 fw-normal text-white  fw-bold d-flex align-items-center gap-1 fs-6">
                <span v-if="selected_status" class="lh-sm">
                  {{ selected_status.replace(/[-_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) }} {{ display_name || 'Applications' }}
                </span>
                <span v-else class="d-flex align-items-center gap-1 lh-sm">
                  <i class="bi bi-people fs-5"></i>
                  All {{ display_name || 'Applications' }}
                </span>
              </p>
            </div>

            <div class="d-flex gap-2">
              <button class="d-none btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark py-1" data-bs-toggle="modal"
                data-bs-target="#create_applicant">
                Create {{ display_name || 'Application' }}
              </button>
              <button class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark py-1"
                :disabled="is_downloading_documents_zip"
                @click="download_all_documents_zip">
                {{ is_downloading_documents_zip ? 'Downloading...' : 'Download all' }}
              </button>
              <button class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark py-1"
                @click="show_filters = !show_filters">
                {{ show_filters ? 'Hide' : 'Show' }} Filters
              </button>
            </div>

          </div>
        </div>
      </div>

      <div class="row g-3 mt-1 px-2">
        <div class="col-12 col-sm-6 col-lg-3">
          <div
            class="card overview-card-bg shadow-sm border-0 rounded h-100"
            :class="{
              'border border-warning border-2': active_compliance_card === 'all',
              'opacity-50': active_compliance_card !== 'all'
            }"
            role="button"
            tabindex="0"
            style="cursor: pointer; pointer-events: auto; transition: opacity 0.2s;"
            @click="set_compliance_card_filter('all')"
            @keydown.enter.prevent="set_compliance_card_filter('all')"
            @keydown.space.prevent="set_compliance_card_filter('all')"
          >
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <div class="text-white text-x fw-bold">Total Applicants</div>
                  <div class="fw-bold text-x text-white">{{ totalApplicants }}</div>
                </div>
                <i class="bi bi-graph-up text-white fs-4"></i>
              </div>
              <div v-if="active_compliance_card === 'all'" class="mt-1 d-flex align-items-center gap-1 text-xxs text-warning fw-bold">
                <i class="bi bi-check-circle-fill"></i> Active
              </div>
            </div>
          </div>
        </div>

        <div class="col-12 col-sm-6 col-lg-3">
          <div
            class="card overview-card-bg shadow-sm border-0 rounded h-100"
            :class="{
              'border border-warning border-2': active_compliance_card === 'qualified',
              'opacity-50': active_compliance_card !== 'all' && active_compliance_card !== 'qualified'
            }"
            role="button"
            tabindex="0"
            style="cursor: pointer; pointer-events: auto; transition: opacity 0.2s;"
            @click="set_compliance_card_filter('qualified')"
            @keydown.enter.prevent="set_compliance_card_filter('qualified')"
            @keydown.space.prevent="set_compliance_card_filter('qualified')"
          >
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <div class="text-white text-x fw-bold">Qualified Applicants</div>
                  <div class="fw-bold text-x text-white">{{qualifiedApplicants}}</div>
                </div>
                <i class="bi bi-check-circle text-success fs-4"></i>
              </div>
              <div v-if="active_compliance_card === 'qualified'" class="mt-1 d-flex align-items-center gap-1 text-xxs text-warning fw-bold">
                <i class="bi bi-check-circle-fill"></i> Active
              </div>
            </div>
          </div>
        </div>

        <div class="col-12 col-sm-6 col-lg-3">
          <div
            class="card overview-card-bg shadow-sm border-0 rounded h-100"
            :class="{
              'border border-warning border-2': active_compliance_card === 'partially_qualified',
              'opacity-50': active_compliance_card !== 'all' && active_compliance_card !== 'partially_qualified'
            }"
            role="button"
            tabindex="0"
            style="cursor: pointer; pointer-events: auto; transition: opacity 0.2s;"
            @click="set_compliance_card_filter('partially_qualified')"
            @keydown.enter.prevent="set_compliance_card_filter('partially_qualified')"
            @keydown.space.prevent="set_compliance_card_filter('partially_qualified')"
          >
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <div class="text-white text-x fw-bold">Partially Qualified</div>
                  <div class="fw-bold text-x text-white">{{partiallyQualifiedApplicants}}</div>
                </div>
                <i class="bi bi-exclamation-triangle text-warning fs-4"></i>
              </div>
              <div v-if="active_compliance_card === 'partially_qualified'" class="mt-1 d-flex align-items-center gap-1 text-xxs text-warning fw-bold">
                <i class="bi bi-check-circle-fill"></i> Active
              </div>
            </div>
          </div>
        </div>

        <div class="col-12 col-sm-6 col-lg-3">
          <div
            class="card overview-card-bg shadow-sm border-0 rounded h-100"
            :class="{
              'border border-warning border-2': active_compliance_card === 'unqualified',
              'opacity-50': active_compliance_card !== 'all' && active_compliance_card !== 'unqualified'
            }"
            role="button"
            tabindex="0"
            style="cursor: pointer; pointer-events: auto; transition: opacity 0.2s;"
            @click="set_compliance_card_filter('unqualified')"
            @keydown.enter.prevent="set_compliance_card_filter('unqualified')"
            @keydown.space.prevent="set_compliance_card_filter('unqualified')"
          >
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <div class="text-white text-x fw-bold">Unqualified Applicants</div>
                  <div class="fw-bold text-x text-white">{{unqualifiedApplicants}}</div>
                </div>
                <i class="bi bi-clock-history text-danger fs-4"></i>
              </div>
              <div v-if="active_compliance_card === 'unqualified'" class="mt-1 d-flex align-items-center gap-1 text-xxs text-warning fw-bold">
                <i class="bi bi-check-circle-fill"></i> Active
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Active filter pill -->
      <div v-if="active_compliance_card !== 'all'" class="d-flex align-items-center gap-2 mt-2 px-1">
        <span class="badge bg-warning text-dark text-xxs py-1 px-2">
          <i class="bi bi-funnel-fill me-1"></i>
          Showing:
          <span v-if="active_compliance_card === 'qualified'">Qualified Applicants</span>
          <span v-else-if="active_compliance_card === 'partially_qualified'">Partially Qualified Applicants</span>
          <span v-else-if="active_compliance_card === 'unqualified'">Unqualified Applicants</span>
        </span>
        <button class="btn btn-link p-0 text-xxs text-muted" @click="set_compliance_card_filter('all')">
          <i class="bi bi-x-circle me-1"></i>Clear filter
        </button>
      </div>

      <!-- Filters Section -->
      <div class="p-1 mt-3 rounded-lg">
        <ApplicationFilter v-show="show_filters" v-model="filters" :entity_options="entity_type"
          :entity_label="`${display_name || 'Entity'} Type`" entity_all_label="All Types"
          search_placeholder="Search by entity name, type, or trust number..."
          :results_text="`${displayed_applications_list.length} of ${sanitized_pending_applications_list.length} applications`"
          :search_key="'search'" :entity_key="'entity_type_id'" :date_from_key="'date_from'" :date_to_key="'date_to'"
          id_prefix="pending_apps" @filters-changed="on_filters_changed" />
      </div>

      <!-- Table Section -->
      <div class="p-1 mt-2 rounded-lg glassy-bg">
        <GlobalTable v-if="displayed_applications_list.length" :key="table_render_key" :headers="table_headers"
          :data="displayed_applications_list" table_id="pending_applications">
          <template #col-Trust_Number="{ row, value }">
            <span v-if="is_master_registered(row) && value" class="badge bg-success text-white text-xxs p-1">
              {{ value }}
            </span>
            <span v-else class="text-muted text-xxs">—</span>
          </template>

          <!-- actions slot -->
          <template #actions="{ row }">
            <div class="d-flex gap-1">
              <button class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
                @click="redirect_to_application_overview(row)">
                View
              </button>
              <button class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark d-none"
                @click="delete_application(row)">
                Delete
              </button>
              <button
                type="button"
                class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark d-none"
                @click="open_move_modal(row)"
              >
                Move
              </button>

            </div>
          </template>
        </GlobalTable>

        <div v-else class="text-center py-5 text-muted">
          <i class="fa-solid fa-inbox fa-3x mb-3"></i>
          <p>No applications found</p>
        </div>
      </div>
    </div>

    <!-- Rest of your modal and footer code remains the same -->
    <button id="open_assign_modal_btn" type="button" class="d-none" data-bs-toggle="modal"
      data-bs-target="#assign_sub_entity_modal">
    </button>

    <!-- Log Modal -->
    <teleport to="body">
      <div v-if="show_log_modal" class="modal fade show d-block" tabindex="-1">
        <div class="modal-dialog">
          <div class="modal-content">
            <div class="modal-header bg-green-dark d-flex justify-content-center">
              <h6 class="modal-title text-center w-100 text-white">Application Timeline</h6>
              <button type="button" class="btn-close position-absolute end-0 me-3" aria-label="Close"
                @click="show_log_modal = false">
              </button>
            </div>
            <div class="timeline-modal-body">
              <ApplicationLog :status="selected_log_status" :user_id="user_id" />
            </div>
          </div>
        </div>
      </div>
    </teleport>
    <!-- Modal Overlay (Backdrop) -->
    <div v-if="show_log_modal" class="modal fade show" @click="show_log_modal = false"></div>

    <!-- Assign Sub Entity Modal -->
    <teleport to="body">
      <div class="modal fade" id="assign_sub_entity_modal" tabindex="-1" aria-labelledby="assign_sub_entity_modal_label"
        aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <!-- Header -->
            <div class="modal-header cute-header d-flex align-items-center justify-content-between px-3 py-2">
              <h5 class="modal-title text-white text-xxs m-0" id="assign_sub_entity_modal_label">Add Sub-{{ display_name || 'Entity' }}</h5>
              <button type="button" class="btn-close modal-btn-close" data-bs-dismiss="modal"
                aria-label="Close"></button>
            </div>

            <!-- Body -->
            <div class="modal-body p-3">
              <!-- Main Entity (readonly for context) -->
              <div class="modal-form-group form-group mb-3">
                <label class="modal-form-label form-label text-xxs">{{ display_name || 'Entity' }} Type</label>
                <input type="text" class="modal-form-control form-control text-xxs" :value="current_form.parent_name"
                  disabled>
              </div>

              <!-- Sub-Entity Type (under the selected parent) -->
              <div class="modal-form-group form-group mb-3">
                <label for="assign_sub_entity_type" class="modal-form-label form-label text-xxs">Sub-{{ display_name || 'Entity' }} Type</label>
                <select id="assign_sub_entity_type"
                  class="modal-form-select form-select text-xs rounded-small p-2 shadow-sm text-x"
                  v-model="assign_sub_entity_type_id" @change="assign_on_sub_entity_change">
                  <option value="" disabled hidden>Select sub-entity type</option>
                  <option v-for="opt in assign_sub_entity_options" :key="opt.id" :value="opt.id">
                    {{ opt.name }}
                  </option>
                </select>
              </div>

              <!-- Forms (populated from the chosen sub-entity type) -->
              <div class="modal-form-group form-group mb-3" v-if="assign_form_list.length">
                <label for="assign_form_select" class="modal-form-label form-label text-xxs">Form</label>
                <select id="assign_form_select"
                  class="modal-form-select form-select text-xs rounded-small p-2 shadow-sm text-x"
                  v-model="assign_selected_form_id">
                  <option value="" disabled hidden>Select form</option>
                  <option v-for="form in assign_form_list" :key="form.id" :value="form.id">
                    {{ form.form_name }}
                  </option>
                </select>
              </div>

              <!-- Sub-Entity Name -->
              <div class="modal-form-group form-group mb-3">
                <label for="assign_entity_name" class="modal-form-label form-label text-xxs">Sub-{{ display_name || 'Entity' }} Name</label>
                <input id="assign_entity_name" type="text" v-model="assign_entity_name"
                  class="modal-form-control form-control text-xs rounded-small p-2 shadow-sm text-x"
                  :placeholder="`Enter Sub-${display_name || 'Entity'} Name`">
              </div>

              <!-- Toggle for showing the email field -->
              <div class="modal-form-group form-group mb-3">
                <div class="form-check form-switch text-xxs d-flex justify-content-start align-items-center">
                  <input type="checkbox" id="email_toggle" v-model="include_email" class="form-check-input"
                    role="switch">
                  <label class="form-check-label ms-2" for="email_toggle">
                    Allow login - Create user account with email invitation
                  </label>
                </div>
                <small class="form-text text-muted text-xxs">
                  When enabled, the entity will be able to log in and complete their own forms
                </small>
              </div>

              <!-- Email -->
              <div class="modal-form-group form-group mb-3" v-if="include_email">
                <label for="assign_email" class="modal-form-label form-label text-xxs">Email</label>
                <input id="assign_email" type="email" v-model="assign_email"
                  class="modal-form-control form-control text-xs rounded-small p-2 shadow-sm text-x"
                  placeholder="Enter Email">
              </div>

              <!-- Footer -->
              <div class="modal-footer border-0 p-2 d-flex justify-content-end">
                <button type="button" class="btn btn-primary-dark btn-sm px-3 text-xxs shadow-sm"
                  @click="save_assign_sub_entity">
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </teleport>

    <!-- Create Application Modal -->
    <teleport to="body">
      <div class="modal fade" id="create_applicant" tabindex="-1" aria-labelledby="create_applicant_label"
        aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content bg-white rounded-3 p-0 shadow-lg">
            <div class="modal-header border-bottom-0 cute-header shadow-lg">
              <p class="modal-title text-white text-xxs" id="create_applicant_label">Create {{ display_name || 'Entity' }}</p>
              <button type="button" class="modal-btn-close btn-close" data-bs-dismiss="modal"
                aria-label="Close"></button>
            </div>

            <form @submit.prevent="create_application">
              <div class="modal-body">
                <!-- Entity type -->
                <div class="modal-form-group form-group mb-3">
                  <label for="entity_type_select" class="modal-form-label form-label text-xxs">{{ display_name || 'Entity' }} type</label>
                  <select id="entity_type_select"
                    class="modal-form-select form-select text-xs rounded-small p-2 shadow-sm text-x"
                    v-model="entity_type_id" @change="get_forms" :disabled="lock_entity_type" required>
                    <option value="" disabled hidden>Select {{ display_name || 'Entity' }} type</option>
                    <option v-for="et in entity_type" :key="et.id" :value="et.id">
                      {{ et.name }}
                    </option>
                  </select>
                </div>

                <!-- Form -->
                <div class="modal-form-group form-group mb-3" v-if="form_list.length">
                  <label for="form_select" class="modal-form-label form-label text-xxs">Form</label>
                  <select id="form_select"
                    class="modal-form-select form-select text-xs rounded-small p-2 shadow-sm text-x"
                    v-model="selected_form_id" required>
                    <option value="" disabled hidden>Select form</option>
                    <option v-for="form in form_list" :key="form.id" :value="form.id">
                      {{ form.form_name }}
                    </option>
                  </select>
                </div>

                <!-- Entity Name -->
                <div class="modal-form-group form-group mb-3">
                  <label for="entity_name" class="modal-form-label form-label text-xxs">{{ display_name || 'Entity' }} Name</label>
                  <input id="entity_name" type="text" v-model="entity_name"
                    class="modal-form-control form-control text-xs rounded-small p-2 shadow-sm text-x"
                    :placeholder="`Enter ${display_name || 'Entity'} Name`" required>
                </div>

                <!-- Toggle for showing the email field -->
                <div class="modal-form-group form-group mb-3">
                  <div class="form-check form-switch text-xxs d-flex justify-content-start align-items-center">
                    <input type="checkbox" id="email_toggle" v-model="include_email" class="form-check-input"
                      role="switch">
                    <label class="form-check-label ms-2" for="email_toggle">
                      Allow login - Create user account with email invitation
                    </label>
                  </div>
                  <small class="form-text text-muted text-xxs">
                    When enabled, the entity will be able to log in and complete their own forms
                  </small>
                </div>

                <!-- Email -->
                <div class="modal-form-group form-group mb-3" v-if="include_email">
                  <label for="assign_email" class="modal-form-label form-label text-xxs">Email</label>
                  <input id="assign_email" type="email" v-model="assign_email"
                    class="modal-form-control form-control text-xs rounded-small p-2 shadow-sm text-x"
                    placeholder="Enter Email">
                </div>
              </div>

              <div class="modal-footer border-0 p-2 d-flex justify-content-end">
                <button type="submit" class="btn btn-primary-dark btn-sm px-3 text-xxs shadow-sm">
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </teleport>
    <!-- Hidden trigger button (keeps your jQuery pattern consistent) -->
    <!-- Hidden trigger button (keep your pattern) -->
    <button
      type="button"
      class="d-none"
      id="open_move_entity_modal_btn"
      data-bs-toggle="modal"
      data-bs-target="#move_entity_modal"
    >
      Open Move Modal
    </button>

    <teleport to="body">
      <div
        class="modal fade"
        id="move_entity_modal"
        tabindex="-1"
        aria-labelledby="move_entity_modal_label"
        aria-hidden="true"
      >
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">

            <div class="modal-header cute-header d-flex align-items-center justify-content-between px-3 py-2">
              <h5 class="modal-title text-white text-xxs m-0" id="move_entity_modal_label">
                Move {{ display_name || 'Entity' }}
              </h5>

              <button
                type="button"
                class="btn-close modal-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                @click="reset_move_modal"
              ></button>
            </div>

            <div class="modal-body p-3">

              <!-- New Entity Type -->
              <div class="modal-form-group form-group mb-3">
                <label class="modal-form-label form-label text-xxs">
                  Select new {{ display_name || 'Entity' }} type
                </label>

                <select
                  class="modal-form-select form-select text-xs rounded-small p-2 shadow-sm text-x"
                  v-model="move_form.new_entity_type_id"
                  @change="on_move_entity_type_change"
                >
                  <option value="" disabled hidden>-- Select entity type --</option>
                  <option v-for="t in entity_type" :key="t.id" :value="t.id">
                    {{ t.name }}
                  </option>
                </select>
              </div>

              <!-- Forms -->
              <div class="mb-3">
                <label class="form-label">Select form</label>

                <select
                  class="modal-form-control form-control text-xxs"
                  v-model="move_form.selected_form_id"
                  @change="on_move_form_change"
                  :disabled="!move_form.new_entity_type_id || move_form_list.length === 0"
                >
                  <option value="" disabled hidden>-- Select form --</option>

                  <option v-if="!move_form.new_entity_type_id" value="" disabled>
                    Select an entity type first
                  </option>

                  <option v-else-if="move_form_list.length === 0" value="" disabled>
                    No forms found for this entity type
                  </option>

                  <option
                    v-else
                    v-for="f in move_form_list"
                    :key="f.id"
                    :value="String(f.id)"
                  >
                    {{ f.name || f.form_name || ('Form #' + f.id) }}
                  </option>
                </select>
              </div>
            </div>

            <div class="modal-footer border-0 p-2 d-flex justify-content-end gap-2">

              <button
                type="button"
                class="btn btn-primary-dark btn-sm px-3 text-xxs shadow-sm"
                @click="submit_move_entity"
              >
                Move &amp; Submit
              </button>
            </div>

          </div>
        </div>
      </div>
    </teleport>
  </div>

</template>
